import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';

import { 
  Wallet, 
  CheckCircle2, 
  AlertCircle, 
  ExternalLink, 
  Loader2, 
  ArrowRight,
  Shield,
  Clock,
  BanknoteIcon,
  RefreshCw
} from 'lucide-react';


export function StripeConnectCard({ getAuthHeaders }) {
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [connectStatus, setConnectStatus] = useState(null);

  useEffect(() => {
    fetchConnectStatus();
  }, []);

  const fetchConnectStatus = async () => {
    try {
      const response = await fetch(`${API}/connect/status`, {
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setConnectStatus(data);
      } else {
        console.error('Failed to fetch connect status');
      }
    } catch (error) {
      console.error('Error fetching connect status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartOnboarding = async () => {
    setActionLoading(true);
    try {
      const currentUrl = window.location.origin;
      const response = await fetch(`${API}/connect/onboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          return_url: `${currentUrl}/dashboard?tab=earnings&connect=success`,
          refresh_url: `${currentUrl}/dashboard?tab=earnings&connect=refresh`
        })
      });

      const result = await safeJson(response);

      if (result.ok && result.data) {
        // Redirect to Stripe's hosted onboarding
        window.location.href = result.data.onboarding_url;
      } else {
        toast.error(result.error || 'Failed to start onboarding');
      }
    } catch (error) {
      console.error('Error starting onboarding:', error);
      toast.error('Failed to connect to payment system');
    } finally {
      setActionLoading(false);
    }
  };

  const handleContinueOnboarding = async () => {
    setActionLoading(true);
    try {
      const currentUrl = window.location.origin;
      const response = await fetch(`${API}/connect/refresh-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          return_url: `${currentUrl}/dashboard?tab=earnings&connect=success`,
          refresh_url: `${currentUrl}/dashboard?tab=earnings&connect=refresh`
        })
      });

      const result = await safeJson(response);

      if (result.ok && result.data) {
        window.location.href = result.data.onboarding_url;
      } else {
        toast.error(result.error || 'Failed to create onboarding link');
      }
    } catch (error) {
      console.error('Error refreshing link:', error);
      toast.error('Failed to connect to payment system');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenDashboard = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API}/connect/dashboard-link`, {
        headers: getAuthHeaders()
      });

      const result = await safeJson(response);

      if (result.ok && result.data) {
        window.open(result.data.dashboard_url, '_blank');
      } else {
        toast.error(result.error || 'Failed to open dashboard');
      }
    } catch (error) {
      console.error('Error opening dashboard:', error);
      toast.error('Failed to connect to payment system');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass-card border-border p-6" data-testid="stripe-connect-card-loading">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </Card>
    );
  }

  // Not started - Show setup prompt
  if (!connectStatus?.has_connect_account) {
    return (
      <Card className="glass-card border-primary/30 p-6" data-testid="stripe-connect-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
            <Wallet className="w-6 h-6 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-heading font-bold text-lg">Set Up Payouts</h3>
              <Badge variant="outline" className="bg-amber-500/10 border-amber-500/30 text-amber-400">
                Required
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Connect your bank account to receive earnings from tips, gifts, and live streams. 
              Powered by Stripe for secure, instant payouts.
            </p>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Shield className="w-4 h-4 text-green-400" />
                <span>Bank-level security</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="w-4 h-4 text-blue-400" />
                <span>Fast transfers</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <BanknoteIcon className="w-4 h-4 text-secondary" />
                <span>No hidden fees</span>
              </div>
            </div>

            <Button
              onClick={handleStartOnboarding}
              disabled={actionLoading}
              className="w-full sm:w-auto"
              data-testid="start-payout-setup-btn"
            >
              {actionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  Set Up Payouts
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  // Onboarding in progress
  if (!connectStatus.onboarding_complete) {
    return (
      <Card className="glass-card border-amber-500/30 p-6" data-testid="stripe-connect-card">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-6 h-6 text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-heading font-bold text-lg">Complete Payout Setup</h3>
              <Badge variant="outline" className="bg-amber-500/10 border-amber-500/30 text-amber-400">
                Incomplete
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Your payout account is not fully set up. Complete the onboarding process to start receiving earnings.
            </p>
            
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm">
                {connectStatus.details_submitted ? (
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-amber-400" />
                )}
                <span className={connectStatus.details_submitted ? 'text-green-400' : 'text-muted-foreground'}>
                  Account details submitted
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                {connectStatus.charges_enabled ? (
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-amber-400" />
                )}
                <span className={connectStatus.charges_enabled ? 'text-green-400' : 'text-muted-foreground'}>
                  Payment processing enabled
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                {connectStatus.payouts_enabled ? (
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-amber-400" />
                )}
                <span className={connectStatus.payouts_enabled ? 'text-green-400' : 'text-muted-foreground'}>
                  Payouts enabled
                </span>
              </div>
            </div>

            {connectStatus.requirements_pending && connectStatus.requirements_pending.length > 0 && (
              <p className="text-xs text-muted-foreground mb-4">
                Additional information may be required to complete verification.
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleContinueOnboarding}
                disabled={actionLoading}
                data-testid="continue-payout-setup-btn"
              >
                {actionLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    Continue Setup
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={fetchConnectStatus}
                disabled={actionLoading}
                data-testid="refresh-status-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Status
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // Fully onboarded - Show success state
  return (
    <Card className="glass-card border-green-500/30 p-6" data-testid="stripe-connect-card">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
          <CheckCircle2 className="w-6 h-6 text-green-400" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-heading font-bold text-lg">Payouts Enabled</h3>
            <Badge variant="outline" className="bg-green-500/10 border-green-500/30 text-green-400">
              Active
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Your payout account is fully set up. You can receive earnings from tips, gifts, and live streams.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-green-400" />
              <span className="text-green-400">Payments enabled</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-green-400" />
              <span className="text-green-400">Payouts enabled</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-green-400" />
              <span className="text-green-400">Verified</span>
            </div>
          </div>

          <Button
            variant="outline"
            onClick={handleOpenDashboard}
            disabled={actionLoading}
            data-testid="open-stripe-dashboard-btn"
          >
            {actionLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Opening...
              </>
            ) : (
              <>
                <ExternalLink className="w-4 h-4 mr-2" />
                View Stripe Dashboard
              </>
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
}
